import {
  Component,
  OnInit,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { WindowService } from 'src/shared/providers/window.service';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { WebsiteConfig } from 'src/shared/services/direct-booking-sites.service';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

@Component({
  selector: 'website-custom-domain',
  templateUrl: './website-custom-domain.component.html',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TidyUiComponentsModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class WebsiteCustomDomainComponent implements OnInit {
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly util = inject(Util);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);

  readonly windowService = inject(WindowService);

  config: WebsiteConfig | null = null;
  isRightSideContent = false;
  submitted = false;
  isSaving = false;

  form = this.fb.group({
    customDomain: ['', [Validators.pattern(/^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/)]],
  });

  async ngOnInit() {
    this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
    this.config = this.navCtrl.getParam('websiteConfig', false);
    if (!this.config) {
      const dialogParams = await this.storage.retrieve('dialog-params');
      this.config = dialogParams?.websiteConfig;
    }
    this.rightSidePanelService.setDialogPageTitle('Custom Domain');
    this.rightSidePanelService.setDialogPageSubtitle(
      this.config?.property_address || this.config?.property_name || ''
    );
    if (this.config?.custom_domain) {
      this.form.patchValue({ customDomain: this.config.custom_domain });
    }
    this.cdr.markForCheck();
  }

  saveDomain = async () => {
    this.submitted = true;
    if (!this.form.valid) {return;}

    this.isSaving = true;
    this.cdr.markForCheck();

    // Mock save delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Hand the new domain back so the site detail page behind this one shows it
    // on its Custom Domain card button when it is re-instantiated on back.
    const customDomain = this.form.value.customDomain || '';
    if (this.config) {
      this.config = { ...this.config, custom_domain: customDomain };
      await this.storage.save('dialog-params', { websiteConfig: this.config });
    }

    this.isSaving = false;
    this.cdr.markForCheck();
    this.util.showSuccess('Custom domain settings saved successfully.');
    this.goBack();
  };

  goBack() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.goBack();
    } else {
      this.navCtrl.back();
    }
  }
}
